import { Box, initialTimerComponentSize, ShadowSettingKey, TimerSettings } from '@lessonup/pins-shared';
import {
  borderRadius,
  Button,
  color,
  IconPause,
  IconPlayFill,
  IconReset,
  styled,
  TextElement,
  useTranslation,
} from '@lessonup/ui-components';
import React from 'react';
import { computeShadowFilter } from '../../utils';
import { useEffectivePinComponentSettings } from '../../utils/pinTheme/PinThemeContext';

export type TimerRenderState = 'new' | 'running' | 'paused' | 'ended';

export interface TimerPinComponentProps {
  timerState: TimerRenderState;
  secondsRemaining: number;
  layout: Box;
  settings?: TimerSettings;
  onAddTime?(): void;
  onReset?(): void;
  onStart?(): void;
  onPause?(): void;
  disabled?: boolean;
}

export const TimerPinComponent: React.FC<TimerPinComponentProps> = ({
  timerState,
  secondsRemaining,
  settings,
  layout,
  onAddTime,
  onReset,
  onStart,
  onPause,
  disabled,
}) => {
  const { t } = useTranslation('timerPinComponent');
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = Math.ceil(secondsRemaining % 60);
  const timerSettings = useEffectivePinComponentSettings('TIMER', {
    settings: settings ?? {},
  });

  const scale = layout.size.width / initialTimerComponentSize.width;

  const computeBackground = (timerState: TimerRenderState) => {
    if (timerState === 'paused') {
      return color.message.warning.fill;
    }
    if (timerState === 'ended') {
      return color.message.negative.fill;
    }
    return timerSettings.backgroundColor;
  };

  return (
    <TimerContainer
      timerState={timerState}
      style={{
        width: `${initialTimerComponentSize.width}px`,
        height: `${initialTimerComponentSize.height}px`,
        borderWidth: timerSettings.borderSize,
        borderColor: timerSettings.borderColor,
        backgroundColor: computeBackground(timerState),
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
      shadow={timerSettings?.shadow}
    >
      <TimerItems>
        {minutes > 0 && (
          <TimerItem
            style={{
              background: timerSettings.foregroundColor,
              color: timerSettings.fontColor,
            }}
          >
            <TextElement textStyle="heading" size="medium">
              {minutes}
            </TextElement>
            <StyledBodyText textStyle="bodyText" size="small">
              {t('minute', { count: minutes })}
            </StyledBodyText>
          </TimerItem>
        )}
        <TimerItem
          style={{
            background: timerSettings.foregroundColor,
            color: timerSettings.fontColor,
          }}
        >
          <TextElement textStyle="heading" size="medium">
            {seconds.toString().padStart(2, '0')}
          </TextElement>
          <StyledBodyText textStyle="bodyText" size="small">
            {t('second', { count: seconds })}
          </StyledBodyText>
        </TimerItem>
      </TimerItems>
      <TimerControls>
        {timerState != 'new' && (
          <Button
            buttonType="neutral"
            buttonShape="rect"
            resizing="hug"
            showStroke={true}
            iconStart={<IconReset />}
            onClick={onReset}
            size="small"
            disabled={disabled}
            style={{
              background: timerSettings.secondaryButton?.backgroundColor,
              borderColor: timerSettings.secondaryButton?.borderColor,
              borderWidth: timerSettings.secondaryButton?.borderSize,
              color: timerSettings?.fontColor,
              pointerEvents: disabled ? 'none' : undefined,
              opacity: disabled ? 0.3 : undefined,
            }}
          />
        )}
        {timerState === 'ended' ? (
          <EndedText textStyle="bodyText" size="small">
            {t('ended')}
          </EndedText>
        ) : (
          <PlayPauseButton
            buttonType={timerState === 'new' ? 'secondary' : 'neutral'}
            buttonShape="rect"
            iconStart={timerState === 'running' ? <IconPause /> : <IconPlayFill />}
            resizing="hug"
            showStroke={true}
            onClick={timerState === 'running' ? onPause : onStart}
            size="small"
            disabled={disabled}
            style={{
              background:
                timerState === 'new'
                  ? timerSettings.primaryButton?.accentColor
                  : timerSettings.primaryButton?.backgroundColor,
              borderColor: timerSettings.primaryButton?.borderColor,
              borderWidth: timerSettings.primaryButton?.borderSize,
              color:
                timerState === 'new'
                  ? timerSettings?.primaryButton?.accentFontColor
                  : timerSettings?.primaryButton?.fontColor,
              pointerEvents: disabled ? 'none' : undefined,
              opacity: disabled ? 0.3 : undefined,
            }}
          />
        )}
        <Button
          buttonType="neutral"
          buttonShape="rect"
          resizing="hug"
          showStroke={true}
          onClick={onAddTime}
          size="small"
          disabled={disabled}
          style={{
            background: timerSettings.secondaryButton?.backgroundColor,
            borderColor: timerSettings.secondaryButton?.borderColor,
            borderWidth: timerSettings.secondaryButton?.borderSize,
            color: timerSettings?.fontColor,
            pointerEvents: disabled ? 'none' : undefined,
            opacity: disabled ? 0.3 : undefined,
          }}
        >
          {t('add30s')}
        </Button>
      </TimerControls>
    </TimerContainer>
  );
};

const TimerContainer = styled.div<{
  timerState: TimerRenderState;
  shadow?: ShadowSettingKey;
}>`
  padding: 0.5rem;
  gap: 0.5rem;
  user-select: none;
  display: flex;
  flex-direction: column;
  border-style: solid;
  border-radius: ${borderRadius.rounded16};
  background-color: ${({ timerState }) => {
    if (timerState === 'paused') {
      return color.message.warning.fill;
    }
    if (timerState === 'ended') {
      return color.message.negative.fill;
    }
    return color.neutralNew.white.fill;
  }};
  ${(props) => props.shadow && `filter: drop-shadow(${computeShadowFilter(props.shadow)});`}
`;

const TimerItems = styled.div`
  display: flex;
  flex: 5 0 0;
  gap: 0.25rem;
`;

const TimerItem = styled.div`
  display: flex;
  background-color: ${color.neutralNew.white.fillSubjacent};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  border-radius: ${borderRadius.rounded8};
`;

const StyledBodyText = styled(TextElement)`
  color: ${color.additional.disabled.text};
`;

const EndedText = styled(TextElement)`
  color: ${color.additional.disabled.text};
  flex: 4 0 0;
  text-align: center;
`;

const TimerControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: 0.5rem;
  height: 1rem;
  flex: 1 0 0;
`;

const PlayPauseButton = styled(Button)`
  flex: 1 0 0;
`;
